import { FC, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  TextField,
  Divider,
  Card,
  CardContent,
  Grid,
  Chip,
} from '@mui/material';
import { Button } from '@ipc/ipc-components';
import CloseIcon from '@mui/icons-material/Close';
import CommentTable from './CommentTable';
import { CommentWithTableProps } from './types';

/**
 * A reusable modal dialog that combines a table view with comment collection
 * @param props Component props
 * @returns CommentWithTable component
 */
const CommentWithTable = <T extends object>({
  isOpen,
  onClose,
  onSubmit,
  title = 'Review Items',
  description,
  submitText = 'Submit',
  cancelText = 'Cancel',
  maxWidth = 'lg',
  fullWidth = true,
  tableData,
  commentRequired = true,
  commentLabel = 'Comment',
  commentPlaceholder = 'Enter your comment here...',
  tableHeight = '400px',
  datasetInfo,
  isLoading = false,
  ...tableProps
}: CommentWithTableProps<T>): React.ReactElement => {
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset the comment and error state when the modal opens
  useEffect(() => {
    if (isOpen) {
      setComment('');
      setError(null);
    }
  }, [isOpen]);

  const handleCommentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setComment(e.target.value);
    // Clear error when user starts typing
    if (error && e.target.value.trim()) {
      setError(null);
    }
  };

  const handleSubmit = () => {
    if (isLoading) return; // Prevent submission if already loading
    if (commentRequired && !comment.trim()) {
      setError('Comment is required');
      return;
    }
    onSubmit(comment);
  };

  const handleClose = () => {
    setComment('');
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      aria-labelledby="comment-with-table-dialog-title"
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        id="comment-with-table-dialog-title"
        sx={{
          m: 0,
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box>
          {title && (
            <Typography variant="h6" component="div">
              {title}
            </Typography>
          )}
          {description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {description}
            </Typography>
          )}
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'text.secondary',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent 
        dividers 
        sx={{ 
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >

        {/* Table Section */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            height: tableHeight,
            '& > div': {
              height: '100%',
            },
          }}
        >
          <CommentTable
            data={tableData}
            columns={tableProps.columns || []}
            moduleName='Comments'
            pageSize={tableProps.pageSize || 5}
            showPagination={true}
            maxWidth="100%"
            // Server-side pagination props
            totalCount={tableProps.totalCount}
            currentPage={tableProps.currentPage}
            onPageChange={tableProps.onPageChange}
            onPageSizeChange={tableProps.onPageSizeChange}
            isLoading={isLoading}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Comment Section */}
        <Box sx={{ p: 3, flexShrink: 0 }}>
          <TextField
            autoFocus
            fullWidth
            label={commentLabel}
            multiline
            rows={3}
            value={comment}
            onChange={handleCommentChange}
            variant="outlined"
            error={!!error}
            helperText={error}
            placeholder={commentPlaceholder}
            InputProps={{
              sx: { 
                borderRadius: '4px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: !!error ? 'error.main' : 'rgba(0, 0, 0, 0.23)',
                },
              },
            }}
          />
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions 
        sx={{ 
          padding: 2, 
          justifyContent: 'flex-end', 
          gap: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button 
          onClick={handleClose}
          variant="secondary"
          size="small"
          disabled={isLoading}
        >
          {cancelText}
        </Button>
        <Button 
          onClick={handleSubmit}
          variant="primary"
          size="small"
          disabled={(commentRequired && !comment.trim()) || isLoading}
        >
          {submitText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CommentWithTable; 